import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { ArrowLeft, History, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../components/ui/card';
import { AuditEntry } from './AuditEntry';
import { useOMS } from '../../../../../apps/hooks/oms/useOMS';
import type { AuditHistoryEntry } from '../../../../../infrastructure/OMSClient';
import { ROUTES } from '../../../../../routes/routes';

export default function AuditPage() {
  const { id: orderId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const oms = useOMS();
  const [auditHistory, setAuditHistory] = useState<AuditHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAuditHistory = async () => {
    if (!orderId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const history = await oms.listAuditHistory(orderId);
      setAuditHistory(history);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch audit history');
      console.error('Error fetching audit history:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditHistory();
  }, [orderId]);

  const handleBackToOrder = () => {
    navigate(ROUTES.oms.orderDetail(orderId));
  };

  const handleRefresh = () => {
    fetchAuditHistory();
  };

  if (!orderId) {
    return (
      <div className="container mx-auto p-4">
        <Card>
          <CardContent className="py-6">
            <div className="text-center text-red-600">
              <AlertCircle className="h-8 w-8 mx-auto mb-2" />
              <p>Order ID not found</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline-primary" onClick={handleBackToOrder} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Order
          </Button>
          <div className="flex items-center mt-4">
            <h1 className="text-2xl font-bold">Audit History</h1>
          </div>
        </div>
      </div>

      {/* Order ID Display */}
      <Card className="mb-4 mt-4">
        <CardHeader className="py-3">
          <CardTitle className="text-lg">Order ID: {orderId}</CardTitle>
        </CardHeader>
      </Card>

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin text-muted-foreground" />
              <p className="text-muted-foreground">Loading audit history...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && !loading && (
        <Card>
          <CardContent className="py-6">
            <div className="text-center text-red-600">
              <AlertCircle className="h-8 w-8 mx-auto mb-2" />
              <p className="font-medium">Error loading audit history</p>
              <p className="text-sm mt-1">{error}</p>
              <Button variant="outline-danger" onClick={handleRefresh} className="mt-3">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !error && auditHistory.length === 0 && (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              <History className="h-8 w-8 mx-auto mb-2" />
              <p className="font-medium">No audit history found</p>
              <p className="text-sm mt-1">
                There are no audit entries for this order yet or completed before implementing audit history. (effective
                starting on 2025-05-23)
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audit History List */}
      {!loading && !error && auditHistory.length > 0 && (
        <div>
          <span className="text-sm text-muted-foreground mb-2">
            Showing most recent changes first ({auditHistory.length} {auditHistory.length === 1 ? 'entry' : 'entries'})
          </span>
          {auditHistory.map((entry, index) => (
            <AuditEntry key={`${entry.updatedTimestamp}-${index}`} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}
